'use client';

import React from 'react';
import {
  Camera, Receipt, Trash2, Plus, Loader2, Sparkles,
  CheckCircle, CheckCircle2, XCircle, Info, AlertTriangle, FileText, Image as ImageIcon,
} from 'lucide-react';
import type { ReceiptAiPayload, ReceiptAiValidation, ReceiptCaptureProfile } from '@/lib/receiptAiAnalysis';

interface ReceiptCaptureSectionProps {
  receiptPhotos: string[];
  receiptPhotoProcessing: boolean;
  receiptValidating: boolean;
  receiptResult: ReceiptAiPayload | null;
  receiptValidation: ReceiptAiValidation | null;
  receiptError: string | null;
  receiptCategoryWarning: string | null;
  receiptCategorySeverity: 'blocking' | 'advisory' | null;
  captureProfile: ReceiptCaptureProfile;
  setCaptureProfile: (profile: ReceiptCaptureProfile) => void;
  isCasualWage: boolean;
  isOnline: boolean;
  accent: string;
  onOpenCamera: () => void;
  onRemovePhoto: (idx: number) => void;
  captureGuide: { title: string; subtitle: string; preferredWidth?: number; preferredHeight?: number };
  selectedCaptureProfile: { label: string; helper: string };
  /** Vehicle registration for vehicle-linked expenses (displayed in AI results) */
  vehicleRegistration?: string | null;
  /** Callback to trigger PDF file picker */
  onUploadPdf?: () => void;
  /** Whether a PDF import is in progress */
  isImportingPdf?: boolean;
  /** PDF import error message */
  importErrorPdf?: string | null;
  /** PDF import progress label */
  importProgressPdf?: string | null;
  /** Callback to trigger image file picker */
  onUploadImages?: () => void;
  /** Whether an image import is in progress */
  isImportingImages?: boolean;
  /** Image import error message */
  importErrorImages?: string | null;
}

export function ReceiptCaptureSection({
  receiptPhotos,
  receiptPhotoProcessing,
  receiptValidating,
  receiptResult,
  receiptValidation,
  receiptError,
  receiptCategoryWarning,
  receiptCategorySeverity,
  captureProfile,
  isCasualWage,
  isOnline,
  accent,
  onOpenCamera,
  onRemovePhoto,
  captureGuide,
  vehicleRegistration,
  onUploadPdf,
  isImportingPdf = false,
  importErrorPdf = null,
  importProgressPdf = null,
  onUploadImages,
  isImportingImages = false,
  importErrorImages = null,
}: ReceiptCaptureSectionProps) {
  return (
    <div>
      <p className="mb-3 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
        {isCasualWage ? (
          <span className="font-semibold text-gray-700 dark:text-gray-200">Attach proof of authorization</span>
        ) : (
          <>
            The camera will automatically detect receipt edges and frame it. {captureGuide.subtitle}
          </>
        )}
      </p>

      {receiptPhotos.length === 0 ? (
        /* Empty state — tap to capture first page */
        <div className="space-y-2">
          <button
            onClick={onOpenCamera}
            disabled={receiptValidating || receiptPhotoProcessing || isImportingPdf}
            className="w-full aspect-[16/9] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 text-sm transition-all border-gray-300 dark:border-gray-600 text-gray-400 hover:border-blue-400 active:scale-[0.98] disabled:opacity-50"
          >
            {isImportingPdf ? (
              <>
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <span className="font-medium">{importProgressPdf || 'Importing PDF...'}</span>
              </>
            ) : (
              <>
                <Camera className="w-8 h-8 text-blue-500" />
                <span className="font-medium">{receiptPhotoProcessing ? 'Processing photo…' : (isCasualWage ? 'Tap to capture proof of authorization' : 'Tap to capture photo of expense')}</span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1">
                  {isCasualWage ? (
                    'e.g. WhatsApp message or signed permission slip'
                  ) : (
                    <><Sparkles className="w-3 h-3" /> AI will extract details automatically</>
                  )}
                </span>
              </>
            )}
          </button>
          <div className="flex gap-2">
            {!isCasualWage && onUploadImages && !isImportingImages && (
              <button
                onClick={onUploadImages}
                disabled={receiptValidating || receiptPhotoProcessing || isImportingPdf}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 transition-all hover:border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-[0.98] disabled:opacity-50"
              >
                <ImageIcon className="w-4 h-4" />
                Upload Images
              </button>
            )}
            {!isCasualWage && onUploadPdf && !isImportingPdf && (
              <button
                onClick={onUploadPdf}
                disabled={receiptValidating || receiptPhotoProcessing || isImportingImages}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 transition-all hover:border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-[0.98] disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                Upload PDF
              </button>
            )}
          </div>
          {isImportingImages && (
            <div className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300">
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              Importing images...
            </div>
          )}
        </div>
      ) : (
        /* Multi-page photo grid */
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white/90 p-2 shadow-sm dark:border-gray-700/70 dark:bg-gray-900/70">
          <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide">
            {receiptPhotos.map((photo, idx) => (
              <div
                key={idx}
                className="group relative flex h-24 w-32 flex-shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
              >
                <img
                  src={photo}
                  alt={`Authorization photo ${idx + 1}`}
                  className="h-full w-full object-cover"
                />

                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />

                {/* Page number badge */}
                <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur-sm">
                  {isCasualWage ? <Camera className="h-3 w-3" /> : <Receipt className="h-3 w-3" />}
                  {isCasualWage ? `Photo ${idx + 1}` : `Page ${idx + 1}`}
                </div>

                {/* Delete button */}
                <button
                  onClick={() => onRemovePhoto(idx)}
                  className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white shadow-sm backdrop-blur-sm transition-all hover:bg-black/70 active:scale-95 z-10"
                  aria-label={`Remove photo ${idx + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>

                <div className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50/95 px-2 py-1 text-[10px] font-semibold text-emerald-700 shadow-sm dark:border-emerald-800 dark:bg-emerald-950/90 dark:text-emerald-300">
                  <CheckCircle className="h-3 w-3" />
                  Captured
                </div>
              </div>
            ))}

            {/* Add more pages button */}
            <button
              onClick={onOpenCamera}
              disabled={receiptValidating || receiptPhotoProcessing || isImportingPdf}
              className="flex h-24 w-32 flex-shrink-0 flex-col items-start justify-between rounded-2xl border border-dashed border-gray-300 bg-gradient-to-br from-white to-slate-50 p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md dark:border-gray-600 dark:from-gray-800 dark:to-gray-900 dark:hover:border-gray-500 disabled:opacity-50"
            >
              <div
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm"
                style={{ backgroundColor: accent }}
              >
                <Plus className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {receiptPhotoProcessing ? 'Processing…' : (isCasualWage ? 'Add Photo' : 'Add Page')}
                </p>
                <p className="mt-0.5 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                  {isCasualWage
                    ? 'Capture another authorization photo.'
                    : (captureProfile === 'continuous' || receiptResult?.layout_hint === 'continuous_invoice')
                      ? 'Capture the next section — overlap by ~2cm with the previous photo so no items are missed.'
                      : 'Capture another receipt page for this expense.'}
                </p>
              </div>
            </button>

            {/* Upload Images button */}
            {!isCasualWage && onUploadImages && !isImportingImages && (
              <button
                onClick={onUploadImages}
                disabled={receiptValidating || receiptPhotoProcessing || isImportingPdf || isImportingImages}
                className="flex h-24 w-32 flex-shrink-0 flex-col items-start justify-between rounded-2xl border border-dashed border-gray-300 bg-gradient-to-br from-white to-slate-50 p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md dark:border-gray-600 dark:from-gray-800 dark:to-gray-900 dark:hover:border-gray-500 disabled:opacity-50"
              >
                <div
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm"
                  style={{ backgroundColor: accent }}
                >
                  <ImageIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Upload Images
                  </p>
                  <p className="mt-0.5 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                    Browse photos from your device.
                  </p>
                </div>
              </button>
            )}

            {/* Upload PDF button */}
            {!isCasualWage && onUploadPdf && (
              <button
                onClick={onUploadPdf}
                disabled={receiptValidating || receiptPhotoProcessing || isImportingPdf || isImportingImages}
                className="flex h-24 w-32 flex-shrink-0 flex-col items-start justify-between rounded-2xl border border-dashed border-gray-300 bg-gradient-to-br from-white to-slate-50 p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md dark:border-gray-600 dark:from-gray-800 dark:to-gray-900 dark:hover:border-gray-500 disabled:opacity-50"
              >
                <div
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm"
                  style={{ backgroundColor: accent }}
                >
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {isImportingPdf ? (importProgressPdf || 'Importing...') : 'Upload PDF'}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                    Import a PDF invoice or receipt.
                  </p>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* AI Analysis Status */}
      {!isCasualWage && (receiptPhotoProcessing || receiptValidating) && (
        <div className="mt-2 flex items-center gap-2 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          <div className="flex-1">
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">
              {receiptPhotoProcessing ? 'Preparing receipt photo…' : 'Analyzing receipt with AI...'}
            </span>
            <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-0.5">
              {receiptPhotoProcessing ? 'Optimizing the capture for stable multi-page uploads' : 'Extracting vendor, amount, layout, and suggested coding'}
            </p>
          </div>
        </div>
      )}

      {!receiptValidating && receiptPhotos.length > 0 && !isOnline && (
        <div className="mt-2 flex items-center gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <Sparkles className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <div>
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">Offline AI queued</span>
            <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">Receipt analysis will run automatically when this expense syncs.</p>
          </div>
        </div>
      )}

      {/* Category mismatch — blocking error */}
      {receiptCategoryWarning && receiptCategorySeverity === 'blocking' && !receiptValidating && (
        <div className="mt-2 flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-xs font-semibold text-red-700 dark:text-red-300">Receipt Mismatch — Cannot Save</span>
            <p className="text-[11px] text-red-600 dark:text-red-400 mt-0.5">{receiptCategoryWarning}</p>
          </div>
        </div>
      )}

      {/* Category advisory — AI disagreed with the (locked) user selection.
          The selection was kept; the disagreement is flagged on the DIN. */}
      {receiptCategoryWarning && receiptCategorySeverity === 'advisory' && !receiptValidating && (
        <div className="mt-2 flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">AI flagged a different category</span>
            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">{receiptCategoryWarning}</p>
          </div>
        </div>
      )}

      {/* Validation result badge */}
      {receiptValidation && !receiptValidating && (
        <div className={`mt-2 p-3 rounded-xl border text-xs space-y-1 ${receiptValidation.match === 'exact' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
          receiptValidation.match === 'close' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' :
            receiptValidation.match === 'mismatch' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :
              'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          }`}>
          <div className="flex items-center gap-1.5">
            {receiptValidation.match === 'exact' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
            {receiptValidation.match === 'close' && <Info className="w-4 h-4 text-yellow-600" />}
            {receiptValidation.match === 'mismatch' && <XCircle className="w-4 h-4 text-red-600" />}
            {(receiptValidation.match === 'no_declared' || receiptValidation.match === 'no_detected') && <Sparkles className="w-4 h-4 text-blue-600" />}
            <span className={`font-semibold ${receiptValidation.match === 'exact' ? 'text-green-700 dark:text-green-300' :
              receiptValidation.match === 'close' ? 'text-yellow-700 dark:text-yellow-300' :
                receiptValidation.match === 'mismatch' ? 'text-red-700 dark:text-red-300' :
                  'text-blue-700 dark:text-blue-300'
              }`}>
              {receiptValidation.match === 'exact' ? 'Amount Verified' :
                receiptValidation.match === 'close' ? 'Close Match' :
                  receiptValidation.match === 'mismatch' ? 'Amount Mismatch' :
                    'AI Extraction Complete'}
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400">{receiptValidation.message}</p>
          {receiptResult && (
            <div className="mt-1.5 pt-1.5 border-t border-current/10 grid grid-cols-2 gap-1 text-gray-600 dark:text-gray-400">
              {receiptResult.vendor_name && <p>Vendor: <span className="font-medium text-gray-800 dark:text-gray-200">{receiptResult.vendor_name}</span></p>}
              {receiptResult.receipt_date && <p>Date: <span className="font-medium text-gray-800 dark:text-gray-200">{receiptResult.receipt_date}</span></p>}
              {receiptResult.layout_hint && <p>Layout: <span className="font-medium text-gray-800 dark:text-gray-200">{receiptResult.layout_hint.replace(/_/g, ' ')}</span></p>}
              {receiptResult.document_type && <p>Doc type: <span className="font-medium text-gray-800 dark:text-gray-200">{receiptResult.document_type.replace(/_/g, ' ')}</span></p>}
              {receiptResult.confidence != null && (
                <p>Confidence: <span className={`font-medium ${receiptResult.confidence >= 0.8 ? 'text-green-600' : receiptResult.confidence >= 0.5 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {Math.round(receiptResult.confidence * 100)}%
                </span></p>
              )}
              {receiptResult.total_amount != null && (
                <p>Detected: <span className="font-medium text-gray-800 dark:text-gray-200">R{receiptResult.total_amount.toFixed(2)}</span></p>
              )}
              {receiptResult.suggested_category && <p>Suggested category: <span className="font-medium text-gray-800 dark:text-gray-200">{receiptResult.suggested_category}</span></p>}
              {receiptResult.suggested_payment_method && <p>Suggested payment: <span className="font-medium text-gray-800 dark:text-gray-200">{receiptResult.suggested_payment_method}</span></p>}
              {receiptResult.suggested_description && <p className="col-span-2">Suggested description: <span className="font-medium text-gray-800 dark:text-gray-200">{receiptResult.suggested_description}</span></p>}
              {vehicleRegistration && <p>Vehicle: <span className="font-medium text-gray-800 dark:text-gray-200">{vehicleRegistration}</span></p>}
            </div>
          )}
        </div>
      )}

      {/* Image import error */}
      {importErrorImages && !isImportingImages && (
        <div className="mt-2 flex items-center gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <div>
            <span className="text-xs font-semibold text-red-600 dark:text-red-400">Image import failed</span>
            <p className="text-[10px] text-red-500 dark:text-red-500 mt-0.5">{importErrorImages}</p>
          </div>
        </div>
      )}

      {/* PDF import error */}
      {importErrorPdf && !isImportingPdf && (
        <div className="mt-2 flex items-center gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <div>
            <span className="text-xs font-semibold text-red-600 dark:text-red-400">PDF import failed</span>
            <p className="text-[10px] text-red-500 dark:text-red-500 mt-0.5">{importErrorPdf}</p>
          </div>
        </div>
      )}

      {/* Error */}
      {receiptError && !receiptValidating && (
        <div className="mt-2 flex items-center gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <div>
            <span className="text-xs font-semibold text-red-600 dark:text-red-400">{receiptError}</span>
            <p className="text-[10px] text-red-500 dark:text-red-500 mt-0.5">You can still fill in the details manually</p>
          </div>
        </div>
      )}
    </div>
  );
}
